import type { TranslationKey } from '../../i18n/he';

/*
 * מודל הקרב כדאטה.
 *
 * זו הצורה של RA2 עצמו, והיא הסיבה שהוא נשאר ניתן לאיזון עשרים שנה: הנזק אינו
 * מספר אחד אלא **מטריצה של warhead מול armor**. טנק לא "חזק"; הוא חזק מול שריון
 * כבד וחלש מול חי"ר מפוזר. יחידה חדשה משנה את האיזון בלי שורת קוד אחת.
 *
 * שרשרת ההפניות: יחידה → נשק → warhead → אחוזים מול כל שריון.
 * `content/validate.ts` מוודא שכל חוליה נפתרת, ו-`combat.test.ts` בודק את החישוב.
 */

// ---------------------------------------------------------------- שריון

export const ARMOR_DEFS = [
    { id: 'flesh', nameKey: 'armor_flesh' },
    { id: 'light', nameKey: 'armor_light' },
    { id: 'heavy', nameKey: 'armor_heavy' },
    { id: 'structure', nameKey: 'armor_structure' },
    /*
     * **שריון אוויר — לא "עוד סוג", אלא הציר השני של המשחק.**
     * מטוס אינו חסין; הוא פשוט לא נמצא ברשימת המטרות של נשק שאינו יודע לירות
     * למעלה. מי שיורה בו כן — עושה זאת מול השריון הזה.
     */
    { id: 'air', nameKey: 'armor_air' },
] as const satisfies readonly { id: string; nameKey: TranslationKey }[];

export type ArmorName = (typeof ARMOR_DEFS)[number]['id'];
export type ArmorId = number;

export const ARMOR_BY_NAME: Readonly<Record<ArmorName, ArmorId>> = Object.fromEntries(
    ARMOR_DEFS.map((def, index) => [def.id, index]),
) as Record<ArmorName, ArmorId>;

// ---------------------------------------------------------------- warheads

export interface WarheadDef {
    readonly id: string;
    /**
     * אחוז מהנזק הבסיסי מול כל סוג שריון. חסר = 100.
     * הטבלה דלילה בכוונה: יחידה חדשה מוסיפה שורה משלה במקום לערוך מטריצה מרכזית.
     */
    readonly versus: Partial<Record<ArmorName, number>>;
    /** רדיוס נזק היקפי ביחידות עולם. 0 = פגיעה נקודתית. */
    readonly splashRadius: number;
    /** רמז ציור לאפקט הפגיעה. לעולם לא לוגיקה. */
    readonly effect: 'spark' | 'blast';
}

export const WARHEAD_DEFS = [
    {
        id: 'smallArms',
        // קליע קל: קטלני לחי"ר, כמעט חסר תועלת מול שריון.
        // חייל יורה גם למעלה, אבל רובה נגד מסוק הוא הטרדה ולא הגנה.
        /*
         * **`heavy` היה 12, ו"כמעט חסר תועלת" היה מה שכתוב כאן ולא מה שקרה.**
         * בזירת עלות שווה ‏14 חיילים (2,800) מחקו 4 טנקים (2,800) ונשארו חמישה
         * מהם חיים, ‏10 חיילים (2,000) ניצחו שני טנקים ושני צוותי נ"ט באותה
         * עלות, וצבא חי"ר טהור ניצח **כל** צבא אחר שנמדד — כלומר לא הייתה שום
         * החלטה מה לייצר.
         *
         * **הסיבה אינה נזק-לאשראי — היא עמידות-לאשראי.** בדיקה חוזרת הפריכה את
         * ההסבר הראשון שנכתב כאן: ב-12% החי"ר היה דווקא ה**גרוע** מבין השלושה
         * בנזק לשנייה לכל 1,000 אשראי מול שריון כבד (25.0, מול 49.1 לטנק
         * ו-101.1 לנ"ט). מה שהחזיק אותו זה שהוא סופג: 1,389 נק"ח אפקטיביות
         * ל-1,000 אשראי מול פגז, מול 429 לטנק. כלומר הוא לא הרג מהר — הוא
         * פשוט לא מת, והנזק הזעיר שלו הספיק לאורך זמן.
         *
         * **6 ו-11 הם אותו מספר בדיוק.** ‏`damageAgainst` מעגלת מטה עם רצפה של
         * 1, והרובה עושה 18: כל ערך בין 3 ל-11 נותן 1 נזק לירייה, ו-12 נותן 2.
         * כלומר הכפתור הזה כבר על הרצפה — אי אפשר להחליש את הרובה מול שריון
         * כבד עוד צעד בלי לגעת ב-`damage`, ב-`burst` או בכלל הרצפה עצמה.
         *
         * **תופעת לוואי מדודה:** הפילבוקס נושא את אותו `rifle`, ולכן הגנת
         * הבסיס מול שריון נחלשה גם היא (‏8 פילבוקסים מול 6 טנקים: 3 שרדו במקום
         * 2). לאור מלכודת 24 זה כנראה לטובה, אבל זו החלטה ולא תופעה.
         *
         * **הרדיוס ההיקפי של הפגז נבדק תחילה ונפסל במדידה** (ראו ההערה שם).
         */
        versus: { flesh: 100, light: 35, heavy: 6, structure: 20, air: 25 },
        splashRadius: 0,
        effect: 'spark',
    },
    {
        id: 'cannon',
        // פגז: מצוין מול רכב, בזבוז מול חייל בודד.
        // פגז נוסע לאט ומתפוצץ בקרקע — הוא לא רודף מסוק. 0 = לא רלוונטי.
        versus: { flesh: 45, light: 100, heavy: 100, structure: 80, air: 0 },
        /*
         * **96 נראה קטן מדי (פחות מחצי אריח) והתברר כלא-הכלי.** ‏288 נבדק
         * בזירה והחמיר את מצב הטנקים בכל הגדלים שנמדדו — טנק בודד, שניים
         * וארבעה. שתי סיבות שנמדדו ולא נוחשו:
         *   1. **ערימה אינה ערימה.** בקרב אמיתי חציון המרחק לשכן הקרוב בקבוצת
         *      חי"ר הוא 183–311, ולא 152 (מינימום ההפרדה): פיזור התצורה מחזיק
         *      אותם 2 אריחים זה מזה. בממוצע יש 0.4–1.3 שכנים בתוך 288, והדעיכה
         *      הלינארית משאירה להם ~17% מהנזק. אין את מי להעניש.
         *   2. **‏`applyImpact` אינו בודק בעלים.** במגע צמוד מרחק חייל-טנק יורד
         *      ל-43, קטן בהרבה מהמרחק בין חייל לחייל — כלומר רדיוס גדול פוגע
         *      בטנקים של היורה **בוודאות גבוהה יותר** מאשר בחי"ר של האויב.
         * ההגדלה תהיה כלי אמיתי רק אם הדעיכה תשתנה או אם תתווסף חסינות ידידותית,
         * ושניהם שינוי מנגנון ב-`sim/`.
         */
        splashRadius: 96,
        effect: 'blast',
    },
    {
        id: 'flak',
        /*
         * נ"מ: קטלני באוויר, כמעט חסר תועלת בקרקע. **זו ההגנה שהופכת מטוס
         * מנשק-על ליחידה** — בלעדיה מסוק אחד מנצח כל בסיס בלי סיכון.
         */
        versus: { flesh: 30, light: 25, heavy: 10, structure: 10, air: 100 },
        splashRadius: 48,
        effect: 'spark',
    },
    {
        id: 'rocket',
        // רקטה אוויר-קרקע: טובה מול רכב ומבנה, בזבוז על חייל בודד.
        versus: { flesh: 40, light: 90, heavy: 85, structure: 100, air: 0 },
        splashRadius: 112,
        effect: 'blast',
    },
    /*
     * **בסוף המערך, תמיד.** מזהה ה-warhead נשמר בתוך כל קליע שבאוויר, ולכן הוא
     * נכנס גם ל-hash וגם לקובץ השמירה — הזזה של רשומה כאן מפוצצת שמירה קיימת
     * עם הנשק הלא נכון.
     *
     * נ"ט: **מעל 100 מול שריון כבד**, וזה מכוון. `cannon` כבר עומד על 100 שם,
     * ולכן warhead שמגיע רק עד 100 אינו "נגד טנקים" אלא "עוד תותח, זול יותר".
     * מה שהופך את היחידה להחלטה הוא דווקא הצד השני של המטריצה: מול חי"ר ומול
     * מבנים היא הגרועה במשחק, ולכן מי שבנה רק אותה לא יכול לסגור משחק.
     */
    {
        id: 'atRocket',
        versus: { flesh: 25, light: 90, heavy: 130, structure: 25, air: 0 },
        // פיצוץ קטן: מספיק כדי להיראות כרקטה, קטן מדי מכדי לנקות ערימת חי"ר.
        splashRadius: 64,
        effect: 'blast',
    },
] as const satisfies readonly WarheadDef[];

export type WarheadName = (typeof WARHEAD_DEFS)[number]['id'];
export type WarheadId = number;

export const WARHEAD_BY_NAME: Readonly<Record<WarheadName, WarheadId>> = Object.fromEntries(
    WARHEAD_DEFS.map((def, index) => [def.id, index]),
) as Record<WarheadName, WarheadId>;

// ---------------------------------------------------------------- נשקים

export interface WeaponDef {
    readonly id: string;
    /** טווח ביחידות עולם (TILE = 256). */
    readonly range: number;
    /** טיקים בין יריות. 20 = ירייה בשנייה. */
    readonly rofTicks: number;
    readonly damage: number;
    readonly warhead: WarheadName;
    /**
     * קליע מעופף, או null לפגיעה מיידית.
     * ירי מהיר וקצר טווח הוא מיידי; פגז נוסע ואפשר "לברוח" ממנו.
     */
    readonly projectile: ProjectileName | null;
    /** כמה יריות ברצף לפני ההמתנה המלאה. 1 = ירייה בודדת. */
    readonly burst: number;
    /**
     * לאילו שכבות הנשק יודע לירות.
     *
     * **זה הכלל היחיד שמפריד בין אוויר לקרקע, והוא בתוכן ולא בקוד.** טנק לא
     * "לא מצליח" לפגוע במסוק — הוא פשוט לא רואה אותו כמטרה חוקית. מכאן ששאלת
     * "מה מגן עליי ממטוסים" נענית בקובץ תוכן, ולא בענף בסימולציה.
     */
    readonly targets: readonly ('ground' | 'air')[];
}

export interface ProjectileDef {
    readonly id: string;
    /** יחידות עולם לטיק. */
    readonly speed: number;
    readonly visual: 'shell' | 'tracer';
}

export const PROJECTILE_DEFS = [
    { id: 'shell', speed: 110, visual: 'shell' },
    { id: 'tracer', speed: 220, visual: 'tracer' },
    { id: 'missile', speed: 150, visual: 'shell' },
    { id: 'flakBurst', speed: 260, visual: 'tracer' },
] as const satisfies readonly ProjectileDef[];

export type ProjectileName = (typeof PROJECTILE_DEFS)[number]['id'];
export type ProjectileId = number;

export const PROJECTILE_BY_NAME: Readonly<Record<ProjectileName, ProjectileId>> =
    Object.fromEntries(PROJECTILE_DEFS.map((def, index) => [def.id, index])) as Record<
        ProjectileName,
        ProjectileId
    >;

export const WEAPON_DEFS = [
    {
        id: 'rifle',
        range: 4 * 256,
        rofTicks: 14,
        damage: 18,
        warhead: 'smallArms',
        projectile: 'tracer',
        burst: 2,
        // חי"ר יורה גם למעלה — הטרדה, לא הגנה. ראו המטריצה.
        targets: ['ground', 'air'],
    },
    {
        id: 'tankCannon',
        range: 5 * 256,
        rofTicks: 32,
        damage: 55,
        warhead: 'cannon',
        projectile: 'shell',
        burst: 1,
        targets: ['ground'],
    },
    {
        id: 'gunshipRockets',
        // טווח ארוך יותר מכל נשק קרקע: זה היתרון של מטוס, וזו גם הסכנה שלו.
        range: 6 * 256,
        rofTicks: 36,
        damage: 70,
        warhead: 'rocket',
        projectile: 'missile',
        burst: 1,
        targets: ['ground'],
    },
    {
        id: 'flakCannon',
        range: 7 * 256,
        rofTicks: 12,
        damage: 30,
        warhead: 'flak',
        projectile: 'flakBurst',
        burst: 3,
        // **אוויר בלבד.** תותח נ"מ שיורה גם בקרקע הוא סתם מגדל שני.
        targets: ['air'],
    },
    {
        id: 'atLauncher',
        /*
         * טווח זהה לתותח הטנק בכוונה: כך הקרב נחתך על מי יורה ראשון ועל כמה
         * עולה כל צד, ולא על ריצה של שניות עד שנכנסים לטווח.
         *
         * **ובעיקר — הפרש הראייה מהטווח חייב להישאר קטן מ-`LEASH_DISTANCE`
         * (4 אריחים).** יחידה במצב Idle רודפת מטרה עד גבול הרצועה ואז מוותרת
         * וחוזרת לעוגן; אם היא רואה רחוק בהרבה ממה שהיא יורה, היא תרדוף, תוותר,
         * תרכוש מחדש — ותיראה תקועה בלולאה. כאן ההפרש הוא אריח אחד (6 מול 5).
         */
        range: 5 * 256,
        // 3 שניות בין רקטות. זה החיסרון שמשלים את הנזק הגבוה: מי שמפספס, ממתין.
        rofTicks: 60,
        damage: 70,
        warhead: 'atRocket',
        projectile: 'missile',
        burst: 1,
        // קרקע בלבד: ההגנה מפני מטוסים היא תותח נ"מ, לא עוד יחידה אוניברסלית.
        targets: ['ground'],
    },
] as const satisfies readonly WeaponDef[];

export type WeaponName = (typeof WEAPON_DEFS)[number]['id'];
export type WeaponId = number;

export const WEAPON_BY_NAME: Readonly<Record<WeaponName, WeaponId>> = Object.fromEntries(
    WEAPON_DEFS.map((def, index) => [def.id, index]),
) as Record<WeaponName, WeaponId>;

// ---------------------------------------------------------------- מטריצה צפופה

/**
 * מטריצת הנזק, מחושבת פעם אחת מהטבלאות הדלילות.
 * `DAMAGE_MATRIX[warhead][armor]` = אחוז.
 */
export const DAMAGE_MATRIX: readonly (readonly number[])[] = WARHEAD_DEFS.map((warhead) =>
    ARMOR_DEFS.map((armor) => warhead.versus[armor.id as ArmorName] ?? 100),
);

/** הנזק בפועל, מעוגל כלפי מטה ולעולם לא פחות מ-1 — ירייה תמיד שווה משהו. */
export function damageAgainst(warheadId: WarheadId, armorId: ArmorId, baseDamage: number): number {
    const row = DAMAGE_MATRIX[warheadId];
    const percent = row?.[armorId] ?? 100;
    return Math.max(1, Math.floor((baseDamage * percent) / 100));
}

export function weaponDef(id: WeaponId): WeaponDef {
    const def = WEAPON_DEFS[id];
    if (!def) throw new Error(`נשק לא מוכר: ${id}`);
    return def;
}

export function warheadDef(id: WarheadId): WarheadDef {
    const def = WARHEAD_DEFS[id];
    if (!def) throw new Error(`warhead לא מוכר: ${id}`);
    return def;
}

export function projectileDef(id: ProjectileId): ProjectileDef {
    const def = PROJECTILE_DEFS[id];
    if (!def) throw new Error(`קליע לא מוכר: ${id}`);
    return def;
}
